import React, { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';

export const commImgSrc = (url) =>
  url?.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;

// CTH-COM-075 — responsive, swipeable multi-image carousel (cover = first image).
export const CommImageCarousel = ({ images = [], title = '', heroOverlay = null, className = '' }) => {
  const trackRef = useRef(null);
  const [idx, setIdx] = useState(0);
  const [zoom, setZoom] = useState(null); // image index or null
  if (!images.length) return null;

  const goTo = (i) => {
    const el = trackRef.current;
    if (!el) return;
    const next = Math.max(0, Math.min(images.length - 1, i));
    el.scrollTo({ left: next * el.clientWidth, behavior: 'smooth' });
  };
  const onScroll = () => {
    const el = trackRef.current;
    if (el) setIdx(Math.round(el.scrollLeft / el.clientWidth));
  };

  return (
    <>
      <div className={`relative overflow-hidden rounded-xl ${className}`} data-testid="comm-carousel">
        <div ref={trackRef} onScroll={onScroll}
             className="flex overflow-x-auto snap-x snap-mandatory scrollbar-none"
             style={{ scrollbarWidth: 'none' }}>
          {images.map((img, i) => (
            <button key={img.file_id || i} type="button"
                    className="w-full shrink-0 snap-center cursor-zoom-in"
                    onClick={() => setZoom(i)} data-testid={`comm-carousel-img-${i}`}>
              <img src={commImgSrc(img.url)} alt={img.alt || title}
                   className="w-full aspect-[16/9] object-contain bg-slate-900/[0.04] select-none" draggable={false} />
            </button>
          ))}
        </div>
        {heroOverlay}
        {images.length > 1 && (
          <>
            <button type="button" aria-label="previous"
                    onClick={() => goTo(idx - 1)} disabled={idx === 0}
                    className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60 disabled:opacity-30 transition-colors"
                    data-testid="comm-carousel-prev">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button type="button" aria-label="next"
                    onClick={() => goTo(idx + 1)} disabled={idx === images.length - 1}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60 disabled:opacity-30 transition-colors"
                    data-testid="comm-carousel-next">
              <ChevronRight className="h-5 w-5" />
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5" data-testid="comm-carousel-dots">
              {images.map((_, i) => (
                <button key={i} type="button" aria-label={`image ${i + 1}`}
                        onClick={() => goTo(i)}
                        className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-5 bg-white' : 'w-1.5 bg-white/60'}`} />
              ))}
            </div>
          </>
        )}
      </div>

      <Dialog open={zoom !== null} onOpenChange={(o) => !o && setZoom(null)}>
        <DialogContent className="max-w-4xl p-1 bg-black/95 border-0" data-testid="comm-lightbox">
          {zoom !== null && (
            <div className="relative">
              <img src={commImgSrc(images[zoom]?.url)} alt={images[zoom]?.alt || title}
                   className="w-full max-h-[80vh] object-contain rounded" />
              {images[zoom]?.alt && (
                <p className="text-xs text-white/80 text-center py-1.5">{images[zoom].alt}</p>
              )}
              {images.length > 1 && (
                <>
                  <button type="button" onClick={() => setZoom((zoom - 1 + images.length) % images.length)}
                          className="absolute left-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/15 text-white flex items-center justify-center hover:bg-white/30"
                          data-testid="comm-lightbox-prev">
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button type="button" onClick={() => setZoom((zoom + 1) % images.length)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/15 text-white flex items-center justify-center hover:bg-white/30"
                          data-testid="comm-lightbox-next">
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              )}
              <button type="button" onClick={() => setZoom(null)}
                      className="absolute right-2 top-2 h-8 w-8 rounded-full bg-white/15 text-white flex items-center justify-center hover:bg-white/30"
                      data-testid="comm-lightbox-close">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export const COMM_CATEGORY_META = {
  evento: { es: 'Evento', en: 'Event', cls: 'bg-violet-100 text-violet-700 border-violet-300' },
  bienestar: { es: 'Bienestar', en: 'Wellbeing', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  capacitacion: { es: 'Capacitación', en: 'Training', cls: 'bg-sky-100 text-sky-700 border-sky-300' },
  noticia: { es: 'Noticia', en: 'News', cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  reconocimiento: { es: 'Reconocimiento', en: 'Recognition', cls: 'bg-pink-100 text-pink-700 border-pink-300' },
  general: { es: 'Comunicación', en: 'Announcement', cls: 'bg-slate-100 text-slate-600 border-slate-300' },
};
